/* eslint-disable no-nested-ternary */
import { useQuery } from '@apollo/client';
import { Query } from '@apollo/client/react/components';
import Button from '@mui/material/Button';
import { cyan, orange, pink, purple } from '@mui/material/colors';
import Grid from '@mui/material/Grid';
import useMediaQuery from '@mui/material/useMediaQuery';
import AddIcon from '@mui/icons-material/Add';
import CommentIcon from '@mui/icons-material/Comment';
import TotalPostsIcon from '@mui/icons-material/Create';
import SettingsIcon from '@mui/icons-material/Settings';
import ViewsIcon from '@mui/icons-material/ShowChart';
import TotalFeaturedIcon from '@mui/icons-material/Star';
import VerifiedIcon from '@mui/icons-material/Verified';
import React, { useState, useEffect } from 'react';
import emoji from '/components/emoji';
import { GET_HOSTING_PLAN } from '../../helpers/graphql/hosting';
import { GET_DASHBOARD_POSTS } from '../../helpers/graphql/posts';
import { GET_USER_STATS } from '../../helpers/graphql/stats';
import { getUser, getRoles } from '../../helpers/token';
import Link from '@mui/material/Link';
import BlogGrid from '../Sidebar/BlogGrid';
import DashboardCard from './DashboardCard';
import FirstPostCard from './FirstPostCard';
import NewBadgePopup from './NewBadgePopup';
import PostsTable from './Stats/PostsTable';
import SmallBox from './Stats/SmallBox';
import { HIVE_ACCOUNT_CREATABLE } from 'helpers/graphql/hive';

const Stats = () => {
  const [roles, setRoles] = useState(null);

  useEffect(() => {
    setRoles(getRoles());
  }, []);

  // Check JWT roles first (fast check)
  const isPaidFromRoles = roles && roles.indexOf('paid') !== -1;

  const privatePosts = useQuery(GET_DASHBOARD_POSTS, {
    variables: { limit: 20 },
    // eslint-disable-next-line no-use-before-define
    ssr: false,
  });
  const hiveAccountCreatable = useQuery(HIVE_ACCOUNT_CREATABLE, {
    fetchPolicy: 'no-cache',
  });

  const hostingPlan = useQuery(GET_HOSTING_PLAN, { ssr: false });

  // Check if user has an active paid subscription from actual blog data
  // This is a fallback when JWT roles haven't been updated yet
  const blogs = hostingPlan.data?.getHostingPlan?.blogs || [];
  const isPaidFromBlogData = blogs.some(blog => {
    const isPaidPlan = blog.plan && blog.plan !== 'trial';
    const isActive = new Date(blog.expiryDate) > new Date();
    return isPaidPlan && isActive;
  });

  // User is paid if either JWT roles OR blog data indicates it
  const hasPaidSubscription = isPaidFromRoles || isPaidFromBlogData;

  const isMobileView = useMediaQuery('(max-width: 540px)');

  return (
    <div style={isMobileView ? undefined : { paddingLeft: '2em' }}>
      <Grid container className="p-1" spacing={0} justifyContent="center">
        <Query query={GET_USER_STATS}>
          {({ data }) => {
            return (
              <>
                <Grid item className="p-1" lg={3} md={6} sm={6} xs={6}>
                  <SmallBox
                    Icon={TotalPostsIcon}
                    title="Posts published"
                    value={
                      data && data.userstats ? data.userstats.total_posts : ''
                    }
                    iconColor={purple[600]}
                    boxColor={purple[400]}
                  />
                </Grid>
                <Grid item className="p-1" lg={3} md={6} sm={6} xs={6}>
                  <SmallBox
                    Icon={ViewsIcon}
                    title="Total views"
                    value={
                      data && data.userstats ? data.userstats.total_views : ''
                    }
                    iconColor={cyan[800]}
                    boxColor={cyan[600]}
                  />
                </Grid>
                <Grid item className="p-1" lg={3} md={6} sm={6} xs={6}>
                  <SmallBox
                    Icon={TotalFeaturedIcon}
                    title="Featured posts"
                    value={
                      data && data.userstats
                        ? data.userstats.total_featured
                        : ''
                    }
                    iconColor={orange[600]}
                    boxColor={orange[400]}
                  />
                </Grid>
                <Grid item className="p-1" lg={3} md={6} sm={6} xs={6}>
                  <SmallBox
                    Icon={CommentIcon}
                    title="Replies received"
                    value={
                      data && data.userstats ? data.userstats.total_replies : ''
                    }
                    iconColor={pink[600]}
                    boxColor={pink[400]}
                  />
                </Grid>
                <Grid item className="p-1" lg={6} md={6} sm={12} xs={12}>
                  <DashboardCard
                    isGrid={!isMobileView}
                    title={`Welcome, ${getUser()}!`}
                    titleSize="h6"
                    content={
                      <div className="postcontent">
                        <p>Welcome to your personal TravelFeed Dashboard!</p>
                        <p>
                          Here you can manage everything related to your blog,
                          for example:
                        </p>
                        <ul>
                          <li>
                            <Link color="textPrimary" href="/dashboard/publish">
                              Write your next awesome travel post
                            </Link>
                          </li>
                          <li>
                            <Link color="textPrimary" href="/dashboard/drafts">
                              Access your drafts and continue where you left off
                            </Link>
                          </li>
                          <li>
                            <Link color="textPrimary" href="/dashboard/posts">
                              View and edit your published posts
                            </Link>
                          </li>
                          <li>
                            <Link color="textPrimary" href="/dashboard/inbox">
                              View and answer replies from your followers
                            </Link>
                          </li>
                          <li>
                            <Link color="textPrimary" href="/dashboard/profile">
                              Edit your profile
                            </Link>
                          </li>
                        </ul>
                      </div>
                    }
                  />
                  {!hostingPlan.loading && !hasPaidSubscription && (
                    <div className="mt-2">
                      <DashboardCard
                        isGrid={!isMobileView}
                        title="Stand out with the verified badge"
                        titleSize="h6"
                        content={
                          <>
                            <div className="tw-flex tw-items-center tw-gap-2 tw-mb-3">
                              <span className="tw-font-medium">
                                {getUser()}
                              </span>
                              <VerifiedIcon
                                className="tw-text-primary-500"
                                style={{ fontSize: '1.2rem' }}
                              />
                            </div>
                            <p className="tw-text-gray-600 dark:tw-text-gray-400 tw-mb-3">
                              Show readers you&apos;re a committed member of our
                              community. Just $19/year.
                            </p>
                            <Link href="/dashboard/upgrade">
                              <Button color="primary" variant="contained">
                                Get Verified
                              </Button>
                            </Link>
                          </>
                        }
                      />
                    </div>
                  )}
                  <div className="mt-2">
                    {!hostingPlan.data || !hostingPlan.data.getHostingPlan ? (
                      <></>
                    ) : hostingPlan.data.getHostingPlan.blogs &&
                      hostingPlan.data.getHostingPlan.blogs.length === 0 ? (
                      <DashboardCard
                        isGrid={!isMobileView}
                        title={`How about ${getUser()}.com?`}
                        titleSize="h6"
                        content={
                          <>
                            <div className="postcontent">
                              <p>
                                Are you ready to become a professional travel
                                blogger and put your blog on your own domain or
                                a free subdomain?
                              </p>
                              <div className="pb-3 pl-3">
                                <div>
                                  {emoji('⌛')} Start your own blog in 1 minute
                                </div>
                                <div>{emoji('🤑')} Ready for monetisation</div>
                                <div>{emoji('✍️')} Fully customisable</div>
                                <div>
                                  {emoji('🧑‍💻')} No coding skills required
                                </div>
                                <div>{emoji('⚡️')} Lightning fast</div>
                                <div>
                                  {emoji('🧩')} Fully integrated with TravelFeed
                                </div>
                              </div>
                              <Link href="/dashboard/hosting">
                                <Button
                                  color="primary"
                                  variant="contained"
                                  className="mr-2"
                                >
                                  Create your first blog
                                </Button>
                              </Link>
                            </div>
                          </>
                        }
                      />
                    ) : (
                      <DashboardCard
                        isGrid={!isMobileView}
                        title="Your Blogs"
                        titleSize="h6"
                        action={
                          <Link href="/dashboard/hosting">
                            <Button className="mr-2">
                              <SettingsIcon />
                              <span className="ml-1">Manage your blogs</span>
                            </Button>
                          </Link>
                        }
                        content={
                          <>
                            <div className="postcontent">
                              {hostingPlan.data.getHostingPlan.blogs.map(
                                blog => {
                                  // For Starter plan, check hostingTrialExpiryDate for hosting status
                                  // For other plans, check expiryDate
                                  const isHostingExpired =
                                    blog.plan === 'starter'
                                      ? blog.hostingTrialExpiryDate &&
                                        new Date(blog.hostingTrialExpiryDate) <
                                          new Date()
                                      : new Date(blog.expiryDate) < new Date();
                                  // A v2 blog has no /api/preview route; it
                                  // lives on its own domain once one serves.
                                  const isV2 = blog.hostingV2Status === 'active';
                                  const origin =
                                    isV2 && blog.domain
                                      ? blog.domain.replace(/\/+$/, '')
                                      : `https://${blog.id}.truvvl.blog`;
                                  return (
                                    <p key={blog.id}>
                                      {isHostingExpired && emoji('⚠️ ')}
                                      <a
                                        href={
                                          isV2 ? `${origin}/` : `${origin}/api/preview`
                                        }
                                        target="_blank"
                                        rel="nofollow noreferrer noopener"
                                      >
                                        {origin.replace(/^https?:\/\//, '')}
                                      </a>
                                    </p>
                                  );
                                },
                              )}
                            </div>
                          </>
                        }
                      />
                    )}
                  </div>
                </Grid>
              </>
            );
          }}
        </Query>
        <Grid item className="p-1" lg={6} md={6} sm={12} xs={12}>
          {privatePosts &&
          privatePosts.data &&
          privatePosts.data.privatePosts &&
          privatePosts.data.privatePosts.length === 0 ? (
            <FirstPostCard isGrid={!isMobileView} />
          ) : (
            <DashboardCard
              isGrid={!isMobileView}
              noBorder
              title="Recent posts"
              titleSize="h6"
              content={
                <>
                  {privatePosts && privatePosts.data && (
                    <PostsTable data={privatePosts.data.privatePosts} />
                  )}
                </>
              }
              action={
                <Link href="/dashboard/publish">
                  <Button className="mr-2">
                    <AddIcon />
                    <span className="ml-1">Create post</span>
                  </Button>
                </Link>
              }
            />
          )}
          {hiveAccountCreatable &&
            hiveAccountCreatable.data &&
            hiveAccountCreatable.data.hiveAccountCreatable && (
              <DashboardCard
                isGrid={!isMobileView}
                title={`Create a free Hive account!`}
                titleSize="h6"
                content={
                  <>
                    <div className="postcontent">
                      <p>
                        A Hive account allows you to reach a larger audience and
                        to monetize your blog.
                      </p>
                      <Link href="/dashboard/create-hive-account">
                        <Button
                          color="primary"
                          variant="contained"
                          className="mr-2"
                        >
                          Learn more
                        </Button>
                      </Link>
                    </div>
                  </>
                }
              />
            )}
          <div className="mt-2">
            <DashboardCard
              isGrid={!isMobileView}
              noBorder
              title="Announcements"
              titleSize="h6"
              content={<BlogGrid />}
            />
          </div>
          {
            // TODO: Card for recent drafts
          }
        </Grid>
      </Grid>
      <NewBadgePopup />
    </div>
  );
};

export default Stats;
